// ─── Custom theme model — shared client/server source of truth ───────
// Drives the per-user custom theme editor. A custom theme is a sparse set of
// overrides (light + dark) on top of a built-in base palette, applied at runtime
// as inline CSS custom properties on <html>. The token NAME allowlist + per-type
// value validation here is the security boundary: only these names, only values
// matching a safe color/shadow grammar, ever reach the DOM or the DB.
/**
 * The editable token set, grouped for the editor UI. Exactly the theme-VARYING
 * color + shadow tokens from index.css :root. Structural tokens (spacing,
 * typography, radii, z-index, durations) are intentionally NOT editable — they
 * don't vary by theme and editing them risks breaking layout. `text-accent` and
 * `accent-danger*` are aliases (they reference other tokens) so they're omitted.
 */
export const THEME_TOKEN_GROUPS = [
    {
        key: 'backgrounds',
        label: 'Backgrounds',
        tokens: [
            { name: 'bg-primary', label: 'App background', type: 'color' },
            { name: 'bg-secondary', label: 'Sidebar background', type: 'color' },
            { name: 'bg-tertiary', label: 'Selected / pressed', type: 'color' },
            { name: 'bg-elevated', label: 'Elevated surface', type: 'color' },
            { name: 'bg-card', label: 'Card', type: 'color' },
            { name: 'bg-card-hover', label: 'Card hover', type: 'color' },
            { name: 'bg-hover', label: 'Hover', type: 'color' },
            { name: 'bg-active', label: 'Active', type: 'color' },
            { name: 'bg-input', label: 'Input / field', type: 'color' },
            { name: 'bg-base', label: 'Recessed base', type: 'color' },
            { name: 'bg-overlay', label: 'Overlay backdrop', type: 'color', hint: 'Usually semi-transparent' },
        ],
    },
    {
        key: 'text',
        label: 'Text',
        tokens: [
            { name: 'text-primary', label: 'Primary text', type: 'color' },
            { name: 'text-secondary', label: 'Secondary text', type: 'color' },
            { name: 'text-tertiary', label: 'Tertiary / muted text', type: 'color' },
            { name: 'text-inverse', label: 'Text on accents', type: 'color' },
        ],
    },
    {
        key: 'borders',
        label: 'Borders',
        tokens: [
            { name: 'border', label: 'Border', type: 'color' },
            { name: 'border-subtle', label: 'Subtle border', type: 'color' },
            { name: 'border-strong', label: 'Strong border', type: 'color' },
            { name: 'border-accent', label: 'Accent border', type: 'color' },
            { name: 'border-focus', label: 'Focus ring', type: 'color' },
        ],
    },
    {
        key: 'primary',
        label: 'Primary accent',
        tokens: [
            { name: 'accent-primary', label: 'Primary', type: 'color' },
            { name: 'accent-primary-dim', label: 'Primary (hover/pressed)', type: 'color' },
            { name: 'accent-primary-soft', label: 'Primary soft tint', type: 'color' },
            { name: 'accent-primary-glow', label: 'Primary glow', type: 'color' },
        ],
    },
    {
        key: 'semantic',
        label: 'Semantic accents',
        tokens: [
            { name: 'accent-secondary', label: 'Secondary', type: 'color' },
            { name: 'accent-secondary-soft', label: 'Secondary soft', type: 'color' },
            { name: 'accent-secondary-glow', label: 'Secondary glow', type: 'color' },
            { name: 'accent-success', label: 'Success', type: 'color' },
            { name: 'accent-success-soft', label: 'Success soft', type: 'color' },
            { name: 'accent-success-glow', label: 'Success glow', type: 'color' },
            { name: 'accent-warning', label: 'Warning', type: 'color' },
            { name: 'accent-warning-soft', label: 'Warning soft', type: 'color' },
            { name: 'accent-warning-glow', label: 'Warning glow', type: 'color' },
            { name: 'accent-error', label: 'Error', type: 'color' },
            { name: 'accent-error-soft', label: 'Error soft', type: 'color' },
            { name: 'accent-error-glow', label: 'Error glow', type: 'color' },
            { name: 'accent-info', label: 'Info', type: 'color' },
            { name: 'accent-info-soft', label: 'Info soft', type: 'color' },
            { name: 'accent-info-glow', label: 'Info glow', type: 'color' },
        ],
    },
    {
        key: 'types',
        label: 'Work-item types',
        tokens: [
            { name: 'type-feature', label: 'Feature', type: 'color' },
            { name: 'type-feature-soft', label: 'Feature soft', type: 'color' },
            { name: 'type-userstory', label: 'User Story', type: 'color' },
            { name: 'type-userstory-soft', label: 'User Story soft', type: 'color' },
            { name: 'type-bug', label: 'Bug', type: 'color' },
            { name: 'type-bug-soft', label: 'Bug soft', type: 'color' },
        ],
    },
    {
        key: 'shadows',
        label: 'Shadows & rings',
        tokens: [
            { name: 'shadow-sm', label: 'Shadow — small', type: 'shadow' },
            { name: 'shadow-md', label: 'Shadow — medium', type: 'shadow' },
            { name: 'shadow-lg', label: 'Shadow — large', type: 'shadow' },
            { name: 'shadow-xl', label: 'Shadow — extra large', type: 'shadow' },
            { name: 'shadow-glow-primary', label: 'Glow — primary', type: 'shadow' },
            { name: 'shadow-glow-secondary', label: 'Glow — secondary', type: 'shadow' },
            { name: 'shadow-glow-success', label: 'Glow — success', type: 'shadow' },
            { name: 'shadow-glow-error', label: 'Glow — error', type: 'shadow' },
        ],
    },
    {
        key: 'chart',
        label: 'Charts',
        tokens: [
            { name: 'chart-grid', label: 'Chart grid lines', type: 'color' },
        ],
    },
];
/** Flat allowlist of every editable token name. */
export const CUSTOM_TOKEN_NAMES = new Set(THEME_TOKEN_GROUPS.flatMap((g) => g.tokens.map((t) => t.name)));
/** name → value-type, for choosing the right validator/control. */
export const THEME_TOKEN_TYPES = Object.fromEntries(THEME_TOKEN_GROUPS.flatMap((g) => g.tokens.map((t) => [t.name, t.type])));
// ─── Value validation (the security boundary) ────────────────────────
// We never string-inject CSS; values are applied via element.style.setProperty
// (the CSSOM rejects invalid grammar) AND persisted. These validators are
// defense-in-depth so only safe color/shadow grammar is ever stored or applied.
/** Chars that must NEVER appear in any token value (style breakout / fetch / JS). */
const FORBIDDEN = /[;{}<>]|url\s*\(|expression\s*\(|javascript:|@import|\/\*|\*\//i;
// #rgb #rgba #rrggbb #rrggbbaa
const HEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
// rgb()/rgba()/hsl()/hsla() with numbers, %, commas, slashes, spaces, decimals
const FUNC_COLOR = /^(?:rgb|rgba|hsl|hsla)\(\s*[0-9.,%/\sdeg]+\)$/i;
const NAMED = /^[a-z]{3,20}$/i; // transparent, currentColor, white, … (kept short + alpha-only)
// box-shadow: only lengths, numbers, the word inset, commas, and color funcs/hex.
const SHADOW = /^(?:none|(?:inset\s+)?[-0-9.a-fA-F#%(),/\srgbahsldegpxremin]+)$/i;
function isValidColor(v) {
    const s = v.trim();
    if (!s || s.length > 64 || FORBIDDEN.test(s))
        return false;
    return HEX.test(s) || FUNC_COLOR.test(s) || NAMED.test(s);
}
function isValidShadow(v) {
    const s = v.trim();
    if (!s || s.length > 200 || FORBIDDEN.test(s))
        return false;
    if (s.toLowerCase() === 'none')
        return true;
    // Must contain at least one length/number and only safe characters.
    return SHADOW.test(s) && /[0-9]/.test(s);
}
/** Is `value` a safe, well-formed value for the named token? */
export function isValidThemeTokenValue(name, value) {
    if (typeof value !== 'string')
        return false;
    if (!CUSTOM_TOKEN_NAMES.has(name))
        return false;
    return THEME_TOKEN_TYPES[name] === 'shadow' ? isValidShadow(value) : isValidColor(value);
}
// ─── Custom theme objects ─────────────────────────────────────────────
export const CUSTOM_THEME_VERSION = 1;
export const MAX_CUSTOM_THEMES = 20;
export const MAX_THEME_NAME_LEN = 60;
/** The built-in palette ids a custom theme can fork from (mirror ThemePalette). */
export const BUILTIN_BASE_PALETTES = ['signal-cyan', 'slate-command', 'verdant-ops', 'indigo-grid', 'rose-prism'];
/** Keep only allowlisted names with valid values from a raw override map. */
export function sanitizeOverrideMap(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object')
        return out;
    for (const [name, value] of Object.entries(raw)) {
        if (isValidThemeTokenValue(name, value))
            out[name] = value;
    }
    return out;
}
/**
 * Validate + normalize an untrusted CustomTheme (from import OR a PATCH body).
 * Returns a clean object, or null if it can't be salvaged (no id/name). Unknown
 * tokens and unsafe values are dropped, not rejected, so a slightly-off import
 * still loads what it can.
 */
export function sanitizeCustomTheme(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const id = typeof r.id === 'string' && r.id.trim() ? r.id.trim().slice(0, 64) : null;
    const nameRaw = typeof r.name === 'string' ? r.name.trim() : '';
    if (!id || !nameRaw)
        return null;
    const base = typeof r.base === 'string' && BUILTIN_BASE_PALETTES.includes(r.base)
        ? r.base : 'signal-cyan';
    return {
        id,
        version: CUSTOM_THEME_VERSION,
        name: nameRaw.slice(0, MAX_THEME_NAME_LEN),
        base,
        light: sanitizeOverrideMap(r.light),
        dark: sanitizeOverrideMap(r.dark),
    };
}
/** Validate + cap a whole library (from a PATCH body). */
export function sanitizeCustomThemeLibrary(raw) {
    if (!Array.isArray(raw))
        return [];
    const seen = new Set();
    const out = [];
    for (const item of raw) {
        const t = sanitizeCustomTheme(item);
        if (t && !seen.has(t.id)) {
            seen.add(t.id);
            out.push(t);
        }
        if (out.length >= MAX_CUSTOM_THEMES)
            break;
    }
    return out;
}
//# sourceMappingURL=theme-tokens.js.map